import { useCallback, useState } from 'react'
import isError from 'lodash/isError'
import toString from 'lodash/toString'
import type { UploadHints } from '@klear/schema/document-processing'
import { createToastConfig, useMutationWithToast } from '@klear/ui/hooks/use-mutations-with-toast'
import { sendDocumentForReview } from '@klear/ui/lib/upload-document'
import { trpc } from '@klear/ui/lib/trpc'

export function useDocumentUploadRequest(token: string | null) {
  return trpc.documentUploadRequest.get.useQuery({ token: token ?? '' }, { enabled: token !== null && token !== '' })
}

const INVALIDATE_REQUEST = ['documentUploadRequest.get']

export function useDocumentUploadRequestMutations() {
  const close = useMutationWithToast(
    trpc.documentUploadRequest.close.useMutation,
    { successTitle: null, successDescription: null, errorTitle: 'Could not finish the upload request' },
    { invalidateQueries: INVALIDATE_REQUEST }
  )
  const setTrader = useMutationWithToast(trpc.documentUploadRequest.setTrader.useMutation, createToastConfig(null), {
    invalidateQueries: INVALIDATE_REQUEST
  })
  return {
    closeRequest: close.mutateAsync,
    setRequestTrader: setTrader.mutateAsync,
    isBusy: close.isPending || setTrader.isPending
  }
}

export type UploadFileStatus = {
  file: File
  status: 'pending' | 'uploading' | 'done' | 'error'
  error?: string
}

export type UploadOutcome = {
  uploadedCount: number
  failed: UploadFileStatus[]
  recordedAgainstRequest: boolean
}

export function useDocumentUpload() {
  const [statuses, setStatuses] = useState<UploadFileStatus[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const utils = trpc.useUtils()

  const reset = useCallback(() => setStatuses([]), [])

  const upload = useCallback(
    async (
      files: File[],
      traderId: number,
      token: string | null,
      hints: UploadHints | null = null
    ): Promise<UploadOutcome> => {
      setIsUploading(true)
      setStatuses(files.map((file) => ({ file, status: 'pending' as const })))

      let uploadedCount = 0
      const failed: UploadFileStatus[] = []
      let recordedAgainstRequest = true

      for (const [index, file] of files.entries()) {
        setStatuses((current) => current.map((row, i) => (i === index ? { ...row, status: 'uploading' } : row)))
        try {
          const sent = await sendDocumentForReview(traderId, file, { hints, uploadRequestToken: token })
          if (sent.uploadRequestRecorded === false) recordedAgainstRequest = false
          uploadedCount += 1
          setStatuses((current) => current.map((row, i) => (i === index ? { ...row, status: 'done' } : row)))
        } catch (error) {
          const message = isError(error) ? error.message : toString(error)
          failed.push({ file, status: 'error', error: message })
          setStatuses((current) =>
            current.map((row, i) => (i === index ? { ...row, status: 'error', error: message } : row))
          )
        }
      }

      setIsUploading(false)
      await utils.documents.invalidate()
      if (token !== null) await utils.documentUploadRequest.get.invalidate()
      return { uploadedCount, failed, recordedAgainstRequest }
    },
    [utils]
  )

  return { upload, statuses, isUploading, reset }
}

export function useSendForReview() {
  const { upload, statuses, isUploading, reset } = useDocumentUpload()
  const send = async (files: File[], traderId: number, hints: UploadHints | null) =>
    (await upload(files, traderId, null, hints)).failed
  return { send, statuses, isUploading, reset }
}

export function failedUploadsMessage(failed: readonly UploadFileStatus[], total: number): string {
  const names = failed.map((row) => `${row.file.name} (${row.error ?? 'failed'})`).join(', ')
  return `${failed.length} of ${total} did not upload: ${names}. Upload again to retry ${failed.length === 1 ? 'it' : 'them'}.`
}
